import React, { Fragment, FunctionComponent, useCallback, useEffect, useState } from "react";
import styles from "./MenuOverlay.module.scss";

import { CLOSE_ALL_OVERLAYS } from "layout/redux/overlay.actions";
import { Helmet } from "react-helmet-async";
import Anchor from "components/Anchor/Anchor";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useWindowDimensions from "hooks/useWindowDimensions";
import combineClasses from "helpers/combine-classes";
import IconViewer from "components/IconViewer/IconViewer";
import Button from "components/Button/Button";
import SocialLinks from "../SocialLinks/SocialLinks";
import { useDispatch } from "react-redux";
import LanguageSelector from "../LanguageSelector/LanguageSelector";

const MenuOverlay: FunctionComponent<any> = (props) => {
    const { width } = useWindowDimensions();
    const dispatch = useDispatch();

    const logoTypeClass = props.header?.logoType ? styles[`logo_type_${props.header.logoType}`] : undefined;
    const logoImageType = props.header?.logoType === "Vertical" ? "containX" : "containY";
    const imageAspectRatio = getImageAspectRatio(undefined, props.header?.logo?.umbracoWidth, props.header?.logo?.umbracoHeight);

    const headerButtonContentType =
        props.header?.headerButtons?.[0] && props.header.headerButtons.length > 0 ? props.header.headerButtons[0].content._contentType : undefined;
    const [currentSubmenu, setCurrentSubmenu] = useState<any>(undefined);

    const onEscKeyInput = useCallback((event) => {
        if (event.keyCode === 27) {
            dispatch({
                type: CLOSE_ALL_OVERLAYS,
            });
        }
    }, []);

    const onCloseButtonClick = useCallback((event) => {
        dispatch({
            type: CLOSE_ALL_OVERLAYS,
        });
    }, []);

    useEffect(() => {
        document.addEventListener("keydown", onEscKeyInput, false);

        return () => {
            document.removeEventListener("keydown", onEscKeyInput, false);
        };
    }, []);

    return (
        <Fragment>
            {/* Lock the scrollbar for the rest of the page on mobile, while drawer is open */}
            {/* TODO: Remember the scroll position and restore it when toggling locked state */}
            {width <= 570 ? <Helmet bodyAttributes={{ class: "is-locked" }} /> : null}

            <nav className={styles.wrapper}>
                <div className={combineClasses(styles.inner, logoTypeClass)}>
                    <div className={styles.header}>
                        {props.header?.logo ? (
                            <Anchor
                                className={styles.image_link}
                                to={props.page?._homeNodeUrl}
                            >
                                <ImageViewer
                                    className={styles.image}
                                    src={props.header.logo.umbracoFile.src}
                                    extensionType="png"
                                    type={logoImageType}
                                    aspectRatioX={imageAspectRatio.aspectRatioX}
                                    aspectRatioY={imageAspectRatio.aspectRatioY}
                                />
                            </Anchor>
                        ) : null}

                        <button
                            className={styles.menu_close_button}
                            onClick={(event) => onCloseButtonClick(event)}
                        >
                            <span></span>
                            <span></span>
                        </button>
                    </div>

                    {typeof currentSubmenu === "undefined" && props.menu ? (
                        <div className={styles.links}>
                            {props.menu?.map((level1Item: any, index: number) => {
                                return (
                                    <span
                                        key={index}
                                        className={styles.item}
                                    >
                                        <Anchor
                                            className={styles.link}
                                            to={level1Item.content.link}
                                        >
                                            <span className={styles.link_label}>{level1Item.content.label}</span>
                                        </Anchor>

                                        {level1Item.content.subMenu && level1Item.content.subMenu.length > 0 ? (
                                            <button
                                                className={styles.link_button}
                                                onClick={() => setCurrentSubmenu(level1Item)}
                                            >
                                                <IconViewer
                                                    className={styles.link_button_icon}
                                                    name="plus"
                                                />
                                            </button>
                                        ) : null}
                                    </span>
                                );
                            })}
                        </div>
                    ) : null}

                    {typeof currentSubmenu !== "undefined" && currentSubmenu.content.subMenu && currentSubmenu.content.subMenu.length > 0 ? (
                        <button
                            className={styles.back_button}
                            onClick={() => setCurrentSubmenu(undefined)}
                        >
                            <IconViewer
                                className={styles.back_icon}
                                name="chevron-circle-left"
                            />
                            {currentSubmenu.content.label}
                        </button>
                    ) : null}

                    {typeof currentSubmenu !== "undefined" && currentSubmenu.content.subMenu && currentSubmenu.content.subMenu.length > 0 ? (
                        <div className={styles.links}>
                            {currentSubmenu.content.subMenu?.map((level2Item: any, index: number) => {
                                return (
                                    <span
                                        key={index}
                                        className={styles.item}
                                    >
                                        <Anchor
                                            className={styles.link}
                                            to={level2Item.content.link}
                                        >
                                            <span className={styles.link_label}>{level2Item.content.label}</span>
                                        </Anchor>
                                    </span>
                                );
                            })}
                        </div>
                    ) : null}

                    <div className={styles.buttons}>
                        {/* Buttons */}
                        {props.headerButtons?.map((button: any, index: number) => {
                            return (
                                <Button
                                    key={index}
                                    to={button.content.link}
                                    className={styles.action_button}
                                    contentType={headerButtonContentType}
                                    {...button.content}
                                >
                                    {button.content.label}
                                </Button>
                            );
                        })}

                        <div className={styles.buttons_social}>
                            {/* Social Links */}
                            <SocialLinks {...props} />

                            {/* Language Selector */}
                            {props?._cultures && Object.keys(props._cultures).length > 1 ? <LanguageSelector /> : null}
                        </div>
                    </div>
                </div>
            </nav>
        </Fragment>
    );
};

export default MenuOverlay;
